import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import localeText from "../../../locale";
import PayPalCheckoutButton from "../../../components/PayPalCheckoutButton/PayPalCheckoutButton";
import ReCaptchaButton from "../../../components/ReCaptchaButton/ReCaptchaButton";
import AreaManagerService from "../../../service/Cadastros/AreaManagerService";
import GetInfoService from "../../../service/GetInfoService";
import { useToastContext } from "../../../contexts/ToastContext/ToastContext";
import ConfimeaFormSuccess from "./ConfimeaFormSuccess";
import { storeConfimeaAssociationRef } from "../../../constants/confimeaAssociation";

const ConfimeaForm = () => {
  const location = useLocation();
  const associationRef = useMemo(() => {
    const param = new URLSearchParams(location.search).get("association");
    return param?.trim() || null;
  }, [location.search]);

  const refereeUsername = associationRef || "confimeaimprese";
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    voucher: "",
    partnerCode: "confimeaimprese",
    acceptTerms: false,
  });

  const [loading, setLoading] = useState(false);
  const [displayPaymentModal, setDisplayPaymentModal] = useState(false);
  const [userState, setUserState] = useState({});
  const [priceForex, setPriceForex] = useState(0);
  const [tokenRecaptcha, setTokenRecaptcha] = useState("");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const toast = useRef(null);
  const { showErrorToast } = useToastContext();
  const areaManagerService = new AreaManagerService();
  const getInfoService = new GetInfoService();

  useEffect(() => {
    async function getCountryAndCurrency() {
      try {
        const userCountry = await getInfoService.getUserCountry();
        const userCurrency = await getInfoService.getUserCurrency();
        setCountry(userCountry);
        setCurrency(userCurrency);
      } catch (e) {
        console.log(e);
      }
    }
    getCountryAndCurrency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Limpar erro quando o usuário começar a digitar
    if (name === "email") {
      setEmailError("");
      if (emailInputRef.current) {
        emailInputRef.current.style.border = "1px solid #ced4da";
      }
    }
    if (name === "password") {
      setPasswordError("");
      if (passwordInputRef.current) {
        passwordInputRef.current.style.border = "1px solid #ced4da";
      }
    }
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return localeText("pleaseFillOutAll");
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return localeText("pleaseEnterValidEmail");
    }
    return "";
  };

  const validatePassword = (password) => {
    if (!password || password.length < 8) {
      return localeText("passwordMin8");
    }
    return "";
  };

  const handleEmailBlur = () => {
    const error = validateEmail(formData.email);
    setEmailError(error);
    if (emailInputRef.current) {
      emailInputRef.current.style.border = error ? "1px solid red" : "1px solid #ced4da";
    }
  };

  const handlePasswordBlur = () => {
    const error = validatePassword(formData.password);
    setPasswordError(error);
    if (passwordInputRef.current) {
      passwordInputRef.current.style.border = error ? "1px solid red" : "1px solid #ced4da";
    }
  };

  const splitFullName = (fullName) => {
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length === 1) {
      return { firstname: nameParts[0], surname: "" };
    }
    const surname = nameParts[nameParts.length - 1];
    const firstname = nameParts.slice(0, -1).join(" ");
    return { firstname, surname };
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseFillOutAll"),
        life: 6000,
      });
      return false;
    }

    if (!formData.email.trim()) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseFillOutAll"),
        life: 6000,
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseEnterValidEmail"),
        life: 6000,
      });
      return false;
    }

    if (!formData.password || formData.password.length < 8) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("passwordMin8"),
        life: 6000,
      });
      return false;
    }

    if (!formData.acceptTerms) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseAgreeTerms"),
        life: 6000,
      });
      return false;
    }

    if (!tokenRecaptcha) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseReverify"),
        life: 6000,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Se houver voucher preenchido, fazer registro direto sem pagamento
    if (formData.voucher && formData.voucher.trim()) {
      await registerManagerWithVoucher();
      return;
    }

    setLoading(true);

    try {
      if (associationRef) {
        storeConfimeaAssociationRef(associationRef);
      }

      const { firstname, surname } = splitFullName(formData.fullName);

      let language = localStorage.getItem("__extraconomy_locale")
        ? localStorage.getItem("__extraconomy_locale")
        : navigator.language;

      switch (language) {
        case "pt":
          language = "PORTUGUESE";
          break;
        case "it":
          language = "ITALIAN";
          break;
        case "es":
          language = "SPANISH";
          break;
        default:
          language = "ENGLISH";
          break;
      }

      const resForex = await getInfoService.getForexRate(
        currency,
        process.env.REACT_APP_BASE_PAYPAL_CURRENCY
      );

      const basePrice = await getInfoService.getPlanListByCurrency(currency);
      const currentPlan = basePrice.result.find((item) => item.id === 2);
      const fullPricePlan =
        currentPlan.price + currentPlan.price * (currentPlan.taxOverPricePercentual / 100);

      let finalPrice;
      if (currency !== process.env.REACT_APP_BASE_PAYPAL_CURRENCY) {
        finalPrice = parseFloat((fullPricePlan * resForex.result).toFixed(2));
      } else {
        finalPrice = parseFloat(fullPricePlan.toFixed(2));
      }

      setPriceForex(finalPrice);

      const user = {
        referee: refereeUsername,
        email: formData.email,
        documentNumber: null,
        fname: firstname,
        lname: surname,
        password1: formData.password,
        language: language,
        currency: currency,
        terms: formData.acceptTerms,
        marketingEmail: false,
        plan: 2,
        country: country,
        internationalCode: "",
        localCode: "",
        phoneNumber: "",
      };

      setUserState(user);
      setDisplayPaymentModal(true);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      showErrorToast({ code: 500000 });
    }
  };

  const registerManagerWithVoucher = async () => {
    try {
      setLoading(true);

      if (associationRef) {
        storeConfimeaAssociationRef(associationRef);
      }

      const { firstname, surname } = splitFullName(formData.fullName);

      let language = localStorage.getItem("__extraconomy_locale")
        ? localStorage.getItem("__extraconomy_locale")
        : navigator.language;

      switch (language) {
        case "pt":
          language = "PORTUGUESE";
          break;
        case "it":
          language = "ITALIAN";
          break;
        case "es":
          language = "SPANISH";
          break;
        default:
          language = "ENGLISH";
          break;
      }

      let location;
      if (!localStorage.getItem("__extraconomy_lat__")) {
        location = await getInfoService.getUserLocation();
      }

      const res = await areaManagerService.postRegisterAreaManager(
        refereeUsername,
        null,
        formData.email,
        null,
        firstname,
        surname,
        formData.password,
        currency,
        language,
        null, // transactionID é null quando há voucher
        country,
        "",
        "",
        "",
        localStorage.getItem("__extraconomy_lat__")
          ? localStorage.getItem("__extraconomy_lat__")
          : location.latitude,
        localStorage.getItem("__extraconomy_long__")
          ? localStorage.getItem("__extraconomy_long__")
          : location.longitude,
        formData.voucher.trim() // voucher
      );

      if (res.messageCode !== 200000) {
        showErrorToast({
          code: res.messageCode,
        });
        setLoading(false);
      } else {
        setRegisteredEmail(formData.email);
        setRegistrationSuccess(true);
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      showErrorToast({ code: 500000 });
    }
  };

  const registerManager = async (transactionID) => {
    try {
      setLoading(true);
      setDisplayPaymentModal(false);

      if (associationRef) {
        storeConfimeaAssociationRef(associationRef);
      }

      const { firstname, surname } = splitFullName(formData.fullName);

      let language = localStorage.getItem("__extraconomy_locale")
        ? localStorage.getItem("__extraconomy_locale")
        : navigator.language;

      switch (language) {
        case "pt":
          language = "PORTUGUESE";
          break;
        case "it":
          language = "ITALIAN";
          break;
        case "es":
          language = "SPANISH";
          break;
        default:
          language = "ENGLISH";
          break;
      }

      let location;
      if (!localStorage.getItem("__extraconomy_lat__")) {
        location = await getInfoService.getUserLocation();
      }

      const res = await areaManagerService.postRegisterAreaManager(
        refereeUsername,
        null,
        formData.email,
        null,
        firstname,
        surname,
        formData.password,
        currency,
        language,
        transactionID,
        country,
        "",
        "",
        "",
        localStorage.getItem("__extraconomy_lat__")
          ? localStorage.getItem("__extraconomy_lat__")
          : location.latitude,
        localStorage.getItem("__extraconomy_long__")
          ? localStorage.getItem("__extraconomy_long__")
          : location.longitude
      );

      if (res.messageCode !== 200000) {
        showErrorToast({
          code: res.messageCode,
        });
        setLoading(false);
      } else {
        setRegisteredEmail(formData.email);
        setRegistrationSuccess(true);
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      showErrorToast({ code: 500000 });
    }
  };

  if (registrationSuccess) {
    return <ConfimeaFormSuccess email={registeredEmail} />;
  }

  return (
    <section id="form" className="confimea-form-section">
      <Toast position="top-right" ref={toast} />
      <div className="confimea-container">
        <div className="confimea-section-header flex flex-column gap-4">
          <div>
            <span className="confimea-section-badge">{localeText("formBadge")}</span>
          </div>
          <div className="confimea-section-title">{localeText("formTitle")}</div>
          <div className="confimea-section-subtitle">{localeText("formSubtitle")}</div>
        </div>

        <div className="confimea-form-container">
          <form className="confimea-form" onSubmit={handleSubmit}>
            <div className="confimea-form-group">
              <label className="confimea-form-label">{localeText("fullName")}</label>
              <input
                type="text"
                name="fullName"
                className="confimea-form-input"
                placeholder={localeText("fullName")}
                value={formData.fullName}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="confimea-form-group">
              <label className="confimea-form-label">{localeText("email")}</label>
              <input
                ref={emailInputRef}
                type="email"
                name="email"
                className="confimea-form-input"
                placeholder={localeText("email")}
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                disabled={loading}
              />
              {emailError && (
                <div className="text-xs mt-1" style={{ color: "red" }}>
                  {emailError}
                </div>
              )}
            </div>

            <div className="confimea-form-group">
              <label className="confimea-form-label">{localeText("formPasswordLabel")}</label>
              <input
                ref={passwordInputRef}
                type="password"
                name="password"
                className="confimea-form-input"
                placeholder={localeText("formPasswordPlaceholder")}
                value={formData.password}
                onChange={handleChange}
                onBlur={handlePasswordBlur}
                disabled={loading}
              />
              {passwordError ? (
                <div className="text-xs mt-1" style={{ color: "red" }}>
                  {passwordError}
                </div>
              ) : (
                <div className="text-xs mt-1" style={{ color: "#7A869A" }}>
                  {localeText("passwordMin8")}
                </div>
              )}
            </div>

            <div className="confimea-form-group">
              <label className="confimea-form-label">Voucher</label>
              <input
                type="text"
                name="voucher"
                className="confimea-form-input"
                placeholder={localeText("voucher")}
                value={formData.voucher}
                onChange={handleChange}
                disabled={loading}
              />
              <div className="text-xs mt-1" style={{ color: "#7A869A" }}>
                Se hai un buono di pagamento, inseriscilo qui.
              </div>
            </div>

            <div className="flex gap-3 align-items-center mb-3">
              <div className="confimea-form-referral-user-label text-xs">Utente referente</div>
              <div className="flex align-items-center gap-2 text-xs font-medium confimea-form-referral-user">
                <i className="pi pi-verified text-green-500"></i>
                <span style={{ color: "rgba(75, 75, 83, 1)" }}>
                  {associationRef ? associationRef : "Confimea Imprese"}
                </span>
              </div>
            </div>

            <div className="confimea-form-checkbox">
              <input
                type="checkbox"
                id="acceptTerms"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                disabled={loading}
              />
              <label htmlFor="acceptTerms">
                {localeText("formAcceptTerms")} {localeText("formTermsLink")}{" "}
                {localeText("formAnd")}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  {localeText("formPrivacyLink")}
                </a>{" "}
                {localeText("formTermsSuffix")}
              </label>
            </div>

            <button
              type="submit"
              className="confimea-btn confimea-btn-register confimea-form-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="pi pi-spin pi-spinner" style={{ marginRight: "0.5rem" }}></i>
                  {localeText("loading")}
                </>
              ) : (
                <span>{localeText("formSubmit")}</span>
              )}
            </button>
            <div className="text-center mt-4">
              <ReCaptchaButton token={tokenRecaptcha} setToken={setTokenRecaptcha} />
            </div>
          </form>
        </div>
      </div>

      <Dialog
        header={localeText("paymentCheckout")}
        visible={displayPaymentModal}
        onHide={() => {
          if (!loading) setDisplayPaymentModal(false);
        }}
        draggable={false}
        breakpoints={{ "960px": "85vw" }}
        style={{ width: "35vw" }}
      >
        {loading ? (
          <div className="text-center">
            <i className="text-6xl my-8 pi pi-spin pi-spinner"></i>
          </div>
        ) : (
          <div className="text-center flex flex-column justify-content-center">
            <PayPalCheckoutButton
              user={userState}
              price={priceForex}
              loading={loading}
              toastUpgrade={toast}
              setLoading={setLoading}
              onRegisterSuccess={registerManager}
            />
          </div>
        )}
      </Dialog>
    </section>
  );
};

export default ConfimeaForm;
